




















































































































































import { Vue, Component, Watch } from 'vue-property-decorator';
import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';

import ContentContainer from '@/components/molecules/ContentContainer/ContentContainer.vue';
import CustomSelect from '@/components/CustomSelect/CustomSelect.vue';
import TextInput from '@/components/TextInput/TextInput.vue';
import TextField from '@/components/atoms/TextField/TextField.vue';
import AnalyticsService from '@/api/AnalyticsService';
import Tabs from '@/components/atoms/Tabs/Tabs.vue';
import Chart from '@/components/Chart/Chart.vue';
import UserIcon from '@/components/molecules/UserIcon/UserIcon.vue';

import { Data, HeaderList, List, Row } from '@/components/Table/TableModel';
import Table from '@/components/molecules/Table/Table.vue';
import TableFilters from '@/components/molecules/TableFilters/TableFilters.vue';
import TableCell from '@/components/molecules/TableCell/TableCell.vue';
import TableHeader from '@/components/molecules/TableHeader/TableHeader.vue';
import TableRow from '@/components/molecules/TableRow/TableRow.vue';
import BasePagination from '@/components/BasePagination/BasePagination.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import ContextMenuOption from '@/components/ContextMenu/ContextMenuOption';

import {
  CustomSelectModel,
  CustomSelectGroup,
  CustomSelectOption,
} from '@/components/CustomSelect/CustomSelectModel';
import { capitalize, treatError } from '@/utils';
import AdminService from '@/api/AdminService';
import { AxiosPromise } from 'axios';
import { treatValue } from '@/utils/moneyTreatments';
import DateUtils from '@/utils/DateUtils';
import { EBusinessStatus } from '@/models/business';
import eventBus from '@/utils/eventBus';
import { formatTotalAndPendingCoins } from '@/utils/coinsHelper';

interface IChartValue {
  x: string;
  y: string;
}

interface IChart {
  chartOptions: ApexOptions;
  chartSeries: { name: string; data: any[] }[];
  key: number;
  data: IChartValue[];
  slug: 'business' | 'customer' | 'merchant';
}

enum ETableCategory {
  business = 'business',
  customer = 'customer',
}

interface IDashboardTab {
  label: string;
  value: ETableCategory;
}

interface IPeriod {
  start: string;
  shortcut: any;
  end: string;
}

const fidelNullLabel = 'Pending';

@Component({
  components: {
    Chart,
    ContentContainer,
    CustomSelect,
    TextInput,
    UserIcon,
    Tabs,
    Table,
    TableFilters,
    TableCell,
    TableHeader,
    TableRow,
    ContextMenu,
    TextField,
  },
})
export default class AdminDashboard extends BasePagination {
  pageName: string = 'Dashboard';
  businessList: List = new List([], []);
  customerList: List = new List([], []);
  service = new AnalyticsService();
  adminService = new AdminService();
  filterByDays = 8;
  apiInterval = 'day';
  charts: IChart[] = [];
  searchableFields = 'searchableFields=business.name';
  filterValuesSeparator = ':';
  disableBindEvents = false;
  period: IPeriod = this.initialPeriod;
  selectedTab: ETableCategory = ETableCategory.business;
  tabs: IDashboardTab[] = [
    {
      label: 'Businesses',
      value: ETableCategory.business,
    },
    {
      label: 'Customers',
      value: ETableCategory.customer,
    },
  ];

  created() {
    this.initCharts();
    this.initBusinessHeader();
    this.initCustomerHeader();
    this.loadList();

    this.$nextTick(() => {
      this.createObserver();
    });
  }

  async initCharts() {
    this.$showLoaderWithLocker();

    this.assignCharts();

    const { to, from } = this.interval;
    try {
      const response = await this.service.getDashboardTransactions({
        to,
        from,
      });

      this.charts.forEach((chart, i) => {
        const correspondentData = response.data.items.find(
          (item: any) => item.label.type === chart.slug,
        );

        if (correspondentData) {
          const { entries } = correspondentData;
          this.charts[i].chartSeries[0].data = entries.map(
            (entry: any) => entry.y,
          );
          this.charts[i].chartOptions.xaxis = {
            categories: entries.map((entry: any) => entry.x),
            type: 'category',
          };
        }

        setTimeout(() => {
          this.charts[i].key++;
        });
      });
    } catch (e) {
      this.$notification(treatError(e));
    } finally {
      this.$hideLoader();
    }
  }

  assignCharts() {
    const defaultChartOptions: ApexOptions = {
      xaxis: {
        categories: [],
        type: 'category',
      },
      yaxis: {
        labels: {
          formatter: (value) => (Number.isInteger(value) ? String(value) : ''),
        },
      },
      chart: {
        toolbar: {
          show: false,
        },
      },
      stroke: {
        curve: 'straight',
      },
      markers: {
        size: 6,
        hover: {
          size: 10,
        },
      },
    };

    this.charts = [
      {
        slug: 'customer',
        data: [],
        key: 0,
        chartOptions: { ...defaultChartOptions },
        chartSeries: [
          {
            name: 'New Customers',
            data: [],
          },
        ],
      },
      {
        slug: 'business',
        data: [],
        key: 0,
        chartOptions: { ...defaultChartOptions },
        chartSeries: [
          {
            name: 'New Businesses',
            data: [],
          },
        ],
      },
    ];
  }

  reloadList(clearList?: boolean, refreshCurrent?: boolean) {
    if (this.refreshing || (!clearList && !this.pagination)) return;

    if (clearList) {
      this.resetPagination();
      // this.tableList.list = [];
    }

    this.$showLoaderWithLocker();
    this.refreshing = true;

    if (!this.sortField) {
      this.sortField = this.defaultSortField;
    }

    if (!this.sortOrder) {
      this.sortOrder = 'DESC';
    }

    this.listRequest({
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      pagination: this.pagination,
      filters: this.query,
      period: this.interval,
    })
      .then((response: any) => {
        const { items, links } = response.data;
        if (refreshCurrent) {
          // refresh page and reinitilize pagination
          this.resetPagination();
          this.pagination = links.next ? decodeURIComponent(links.next) : '';
          if (!links.next) this.maxItems = true;
          this.tableList.list = this.normalizeItems(items);
          this.isLoading = false;
        } else {
          // refresh page and add items
          this.tableList.list = this.tableList.list.concat(
            this.normalizeItems(items),
          );
          if (!links.next) this.maxItems = true;
          this.pagination = links.next ? decodeURIComponent(links.next) : '';
        }
      })
      .finally(() => {
        this.$hideLoader();
        this.isLoading = false;
        this.refreshing = false;
      });
  }

  initBusinessHeader() {
    const header = [
      new HeaderList(`Business`, 'none', '12px 12px 12px 28px'),
      new HeaderList(`name`, 'business.name'),
      new HeaderList(`Location`, 'business.city', '12px'),
      new HeaderList(`Transactions`, 'totalTransactions', '12px'),
      new HeaderList(`Customers`, 'totalCustomers', '12px'),
      new HeaderList(`Created At`, 'business.createdAt', '12px'),
      new HeaderList(`Integration Type`, 'provider', '12px'),
      new HeaderList(``, 'none', '12px'),
    ];

    this.businessList.header = header;
  }

  initCustomerHeader() {
    const header = [
      new HeaderList(`Picture`, 'none', '12px 12px 12px 32px'),
      new HeaderList(`Customer name`, 'customer_name'),
      new HeaderList(`Total spent`, 'customer_totalSpent'),
      new HeaderList(`Total Coins`, 'customer_totalCoins', '12px'),
      new HeaderList(`Joined At`, 'customer_createdAt', '12px'),
      new HeaderList(``, 'none', '12px'),
    ];

    this.customerList.header = header;
  }

  onPeriodInput(newValue: IPeriod) {
    if (newValue && newValue.start && newValue.end) {
      const value = newValue;
      value.start = `${newValue.start}Z`;
      value.end = `${newValue.end}Z`;
      value.shortcut = newValue.shortcut;

      this.period = value;
    }
  }

  @Watch('period')
  onPeriod() {
    this.initCharts();
    this.reloadList(true, true);
  }

  @Watch('selectedTab')
  loadList(newTab: ETableCategory = this.selectedTab) {
    this.searchableFields =
      newTab === ETableCategory.business
        ? 'searchableFields=business.name&searchableFields=business.city'
        : 'searchableFields=customer.name';
    this.clearPaginationParams();

    this.reloadList(true, true);
  }

  clearPaginationParams() {
    this.search = '';
    this.query = '';
    this.sortField = '';
    this.sortOrder = '';
  }

  get searchPlaceholder() {
    const placeholders: Record<ETableCategory, string> = {
      [ETableCategory.business]: 'Search by business name or location',
      [ETableCategory.customer]: 'Search by customer name',
    };

    return placeholders[this.selectedTab];
  }

  get tableList(): List {
    const lists: Record<ETableCategory, List> = {
      [ETableCategory.business]: this.businessList,
      [ETableCategory.customer]: this.customerList,
    };

    return lists[this.selectedTab];
  }

  get listRequest() {
    const requests: Record<
      ETableCategory,
      (...props: any[]) => AxiosPromise<any>
    > = {
      [ETableCategory.business]: this.adminService.getBusinessesList.bind(
        this.adminService,
      ),
      [ETableCategory.customer]: this.adminService.getCustomers.bind(
        this.adminService,
      ),
    };

    return requests[this.selectedTab];
  }

  get normalizeItems() {
    const normalizers: Record<ETableCategory, (items: any[]) => Data> = {
      [ETableCategory.business]: this.normalizeBusinesses,
      [ETableCategory.customer]: this.normalizeCustomers,
    };

    return normalizers[this.selectedTab];
  }

  get defaultSortField() {
    const sortField: Record<ETableCategory, string> = {
      [ETableCategory.business]: 'business.createdAt',
      [ETableCategory.customer]: 'customer_createdAt',
    };

    return sortField[this.selectedTab];
  }

  normalizeBusinesses(items: any[]) {
    return items.map((business: any) => ({
      id: business.id,
      picture: business.logo.publicPath.small,
      name: business.name,
      status: business.status,
      mail: business.manager.mail,
      managerName: business.manager.fullName,
      phone: business.phone,
      totalTransactions: business.totalTransactions,
      totalCustomers: business.totalCustomers,
      address: [business.city, business.state, business.country]
        .filter((n) => n)
        .join(', '),
      createdAt: DateUtils.formatISODate(business.createdAt),
      provider: capitalize(business.provider || 'None'),
    }));
  }

  normalizeCustomers(items: any[]) {
    return items.map((row: any) => ({
      id: row.customer.id,
      name: `${row.customer.name} ${row.customer.lastname}`,
      picture: row.image.id
        ? JSON.parse(row.image.publicPath).small
        : `${row.customer.name[0]}${row.customer.lastname[0]}`,
      spent: `$${treatValue(row.customer.totalSpent) || '0'}`,
      reward: treatValue(row.customer.totalRewards) || '0',
      visits: row.customer.totalVisits || '0',
      coins: treatValue(row.customer.totalCoins) || '0',
      totalCoins: formatTotalAndPendingCoins(
        treatValue(row.customer.totalCoins) || '0',
        treatValue(row.customer.pendingCoins) || '0',
      ),
      last_seen: DateUtils.formatISODate(row.customer.lastSeen),
      joined: DateUtils.formatISODate(row.customer.createdAt),
    }));
  }

  getOptionsByData(business: any) {
    const options: ContextMenuOption[] = [
      {
        label: 'Impersonate',
        action: 'impersonate',
      },
    ];

    Object.keys(EBusinessStatus).forEach((key) => {
      const statusesObj = EBusinessStatus as any;
      const item = statusesObj[key];

      if (item === EBusinessStatus.unknown) return;

      if (business.status !== item.toLowerCase()) {
        options.push({
          label: `Set as ${item}`,
          action: 'setStatus',
          props: [item],
        });
      }
    });

    if (business.fidel === fidelNullLabel) {
      options.push({
        label: 'Send to Fidel',
        action: 'connectFidel',
      });
    } else {
      options.push({
        label: 'Force Update to Fidel',
        action: 'connectFidel',
      });
    }

    return options;
  }

  optionSelected(action: string, props: Array<any>, business: any): void {
    switch (action) {
      case 'impersonate':
        this.impersonate(business);
        break;
      case 'setStatus':
        this.setStatus(props[0], business);
        break;
      case 'connectFidel':
        this.connectFidel(business);
        break;
      default:
        break;
    }
  }

  openAddMID(data: any) {
    const modalConfig = {
      isOpen: true,
      component: 'addMerchantId',
      data,
    };

    eventBus.$emit('setSideModalVisible', modalConfig);
  }

  connectFidel(business: any) {
    this.$showLoaderWithLocker();
    this.adminService.connectFidel(business.id).then((res) => {
      this.$hideLoader();
      if (res.status === 200) {
        this.$notification('Business info sent to Fidel successfully');
        this.reloadList(true, true);
      }
    });
  }

  setStatus(status: any, business: any) {
    this.$showLoaderWithLocker();
    this.adminService
      .setBusinessStatus(business.id, { status: status.toLowerCase() })
      .then((res) => {
        this.$hideLoader();
        if (res.status === 200) {
          this.$notification(`Business set as ${status} succesfully`);
          this.reloadList(true, true);
        }
      });
  }

  impersonate(business: any) {
    this.adminService
      .impersonate({
        mail: business.mail,
        business: business.id,
      })
      .then((res) => {
        const { access_token } = res.data;
        this.service.setCredentials(access_token, true);

        (window.top || window).location.assign('/home');
      });
  }

  detail(event: any) {
    const modalConfig = {
      isOpen: true,
      component: 'adminCustomerDetails',
      data: event,
    };
    eventBus.$emit('setSideModalVisible', modalConfig);
  }

  createObserver() {
    const target = this.$refs.target as HTMLElement;

    const observer = new IntersectionObserver((entries) => {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);

      if (isIntersecting && !!this.pagination) {
        this.reloadList();
      }
    });

    if (target) {
      observer.observe(target);
    }
  }

  get getLabelByPeriod() {
    const { start, end } = this.period;
    const format = (date: string) => DateUtils.formattedDate({ date });

    return `${format(start)} - ${format(end)}`;
  }

  get initialPeriod() {
    return {
      end: dayjs().utc().toISOString(),
      start: dayjs().utc().add(-7, 'days').toISOString(),
      shortcut: '',
    };
  }

  get interval() {
    return {
      to: this.period.end,
      from: this.period.start,
    };
  }

  get timestemp() {
    const { start, end } = this.period;
    const toUTC = (date: string) => dayjs(date).utc().toDate();

    const diffInTime = toUTC(start).getTime() - toUTC(end).getTime();
    const goBackDays = Math.abs(diffInTime / (1000 * 3600 * 24));

    const daysSorted = [toUTC(end).toDateString()];

    for (let i = 1; i < goBackDays; i++) {
      const newDate = toUTC(end);
      newDate.setDate(toUTC(end).getDate() - i);
      daysSorted.push(newDate.toDateString());
    }

    return daysSorted.reverse();
  }

  getSeriesDataFromEntries(entries: IChartValue[]) {
    return this.timestemp.map((day) => {
      const dates = entries.map((entry: IChartValue) =>
        new Date(`${entry.x}T00:00:00`).toDateString(),
      );

      const index = dates.indexOf(day);
      if (index >= 0) {
        return entries[index].y;
      }

      return 0;
    });
  }

  openBusinessDetail(event: any) {
    const modalConfig = {
      isOpen: true,
      component: 'adminBusinessDetails',
      data: event,
    };
    eventBus.$emit('setSideModalVisible', modalConfig);
  }

  get periodFilterModel() {
    const actionGroup = new CustomSelectGroup([
      new CustomSelectOption('8', '7 days', true),
      new CustomSelectOption('16', '15 days'),
      new CustomSelectOption('31', '30 days'),
      new CustomSelectOption('90', '3 months'),
    ]);

    return new CustomSelectModel(
      [actionGroup],
      `By period`,
      false,
      false,
      false,
      true,
    );
  }

  async periodChanged(data: any) {
    const { uuid } = data[0];
    this.filterByDays = Number(uuid);

    this.initCharts();
    this.reloadList(true, true);
  }
}
